import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ReplayIcon from "@mui/icons-material/Replay";
import { userRequest } from "../../api/requestMethods";
import { trackReorder } from "../../utils/analytics";
import {
  cartLinesReplacedBy,
  isReorderable,
  replaceCartQuestion,
  reorderIntoCart,
  reorderNotice,
} from "../../utils/reorderIntoCart";

/**
 * OrderAgainButton: "Order again in one tap" (2 Nov 2026).
 *
 * Self contained, like ReportIssueButton: one tap asks the server to rebuild
 * the order as a cart priced today (GET /api/orders/:orderId/reorder), puts
 * the lines in that vendor's cart through utils/reorderIntoCart, says in one
 * message what could not come back, and opens the cart to review. When that
 * cart already holds items it asks before replacing them. The order
 * list and the order page each render it with one line.
 *
 * `compact` is the order-list row size; the order page uses the full size.
 */
const OrderAgainButton = ({ order, surface = "account", compact = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [rebuilding, setRebuilding] = useState(false);
  /* A successful tap navigates to the cart, which unmounts this button
     before the request's `finally` runs; nothing is set on it after that.
     A layout effect, because React 17 runs a plain effect's cleanup only
     after the unmount, too late for the `finally` that follows the push. */
  const mountedRef = useRef(true);
  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (!isReorderable(order)) return null;
  const orderId = String(order._id);

  const handleOrderAgain = async () => {
    if (rebuilding) return;
    setRebuilding(true);
    try {
      const { data: reorder } = await userRequest.get(`/api/orders/${orderId}/reorder`);
      const replaced = dispatch((_dispatch, getState) => cartLinesReplacedBy(reorder, getState()));
      if (replaced && !window.confirm(replaceCartQuestion(reorder, replaced))) return;
      const { added, location } = dispatch(reorderIntoCart(reorder));
      trackReorder({ surface, vendorType: reorder.vertical, itemCount: added, orderId });
      const notice = reorderNotice(reorder);
      toast[notice.tone](notice.text);
      if (added) history.push(location);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "We could not rebuild this order right now. Please try again."
      );
    } finally {
      if (mountedRef.current) setRebuilding(false);
    }
  };

  return (
    <Trigger
      type="button"
      $compact={compact}
      onClick={handleOrderAgain}
      disabled={rebuilding}
      aria-busy={rebuilding}
      aria-label={`Order again: order ${orderId.slice(-9)}`}
    >
      <ReplayIcon style={{ fontSize: compact ? 15 : 18 }} />
      {rebuilding ? "Adding..." : "Order again"}
    </Trigger>
  );
};

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #03660c;
  border-radius: ${(p) => (p.$compact ? "8px" : "6px")};
  background: #03660c;
  color: #ffffff;
  font-family: inherit;
  font-size: ${(p) => (p.$compact ? "12.5px" : "0.875rem")};
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  padding: ${(p) => (p.$compact ? "7px 12px" : "10px 18px")};
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease;

  &:hover:not(:disabled) {
    background: #02550a;
    border-color: #02550a;
  }
  &:focus-visible {
    outline: 2px solid #03660c;
    outline-offset: 2px;
  }
  &:disabled {
    cursor: progress;
    opacity: 0.75;
  }
`;

export default OrderAgainButton;
